import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { ArrowLeft, Heart, ThumbsDown, Repeat2, Share2, Send, ChevronDown, Calendar, Users, UserCheck, Bell, MessageSquare, Edit3, MoreVertical, Pin, Trash, LogOut } from 'lucide-react';
import PostDetailNavbar from '../components/desktop/PostDetailNavbar';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../utils/postUrls';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { useDynamicBackground } from '../hooks/useDynamicBackground';
import LoadingScreen from '../components/desktop/LoadingScreen';
import { canModeratePost, getEditorType, getPinnerType } from '../utils/permissions';
import { EditPostModal } from '../components/desktop/EditPostModal';
import { PostBadges } from '../components/desktop/PostBadges';

import { postEventBus } from '../lib/postEventBus';
import { usePostSync } from '../hooks/usePostSync';
import { commentEventBus } from '../lib/commentEventBus';
import { useCommentSync } from '../hooks/useCommentSync';
import { getBackground, getCardBackground, calculateNavbarBg } from '../utils/backgroundHelpers';
import { cacheTheme, getCachedTheme } from '../utils/themeCache';
import { transformColor, type ColorTransformOptions } from '../utils/colorTransform';
import resolveAvatar from '../lib/resolveAvatar';
import { normalizeLikeCounts } from '../components/shared/hooks/useFeedData';

import { fetchWithAuth } from '../lib/fetchWithAuth';
type User = {
  id: number | string;
  username: string;
  displayName?: string;
  avatar?: string;
};

type Comment = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  content: string;
  timestamp: string;
  likes: number;
  dislikes: number;
  liked: boolean;
  disliked: boolean;
};

type Post = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  title?: string;
  slug?: string;
  content: string;
  mediaUrl?: string;
  imageUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  tags?: string[];
  likes: number;
  dislikes: number;
  disliked?: boolean;
  comments: number;
  shares: number;
  timestamp: string;
  liked: boolean;
  createdAt: string;
  community?: {
    id: string;
    name: string;
    nameSlug: string;
    communityCustomization?: any; // Community theme customization
  };
};

export default function PostView() {
  const { communitySlug, postSlug } = useParams<{ communitySlug: string; postSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, currentPreset } = useTheme();
  const { user: authUser, loading: authLoading, logout, setShowLoginModal } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const backgroundHeight = useDynamicBackground();
  const [post, setPost] = useState<Post | null>(null);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [displayedComments, setDisplayedComments] = useState<Comment[]>([]);
  const [commentPage, setCommentPage] = useState(1);
  const currentUser = authUser;
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'following' | 'friends'>('all');
  const [showShareModal, setShowShareModal] = useState(false);
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // Try to load animations from cached site theme first
    const cachedSiteTheme = getCachedTheme('site');
    if (cachedSiteTheme?.animations) {
      return cachedSiteTheme.animations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const [loading, setLoading] = useState(true);
  const [showModerateMenu, setShowModerateMenu] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [communityCustomization, setCommunityCustomization] = useState<any>(null);

  // Fetch community customization using communitySlug from URL
  useEffect(() => {
    if (communitySlug) {
      fetchWithAuth(`/api/community/slug/${communitySlug}`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          if (data.community?.communityCustomization) {
            const customization = data.community.communityCustomization;
            // Community uses 'presetTheme' field, not 'preset'
            const communityPreset = customization.presetTheme || customization.preset || 'ocean-turtle';

            setCommunityCustomization(customization);

            // Set theme animations based on community's preset
            const animations = getThemeAnimations(communityPreset);
            setThemeAnimations(animations);
          }
        })
        .catch(err => console.error('❌ [PostView] Failed to fetch community:', err));
    }
  }, [communitySlug]);

  // LOCAL THEME: Use community theme from fetched customization, fallback to site theme
  // IMPORTANT: Transform colors when colorSpace is enabled (like CommunityClient does)
  const localTheme = React.useMemo(() => {
    // If we have community customization, use it and transform it
    if (communityCustomization) {
      const customization = communityCustomization;

      // Helper to convert hex to rgba with opacity
      const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
      };

      const colorSpace = customization.colorSpace || 'Off';

      // If color space is "Off", return raw colors without transformation
      if (colorSpace === 'Off') {
        const navbarBg = customization.navbarBg
          ? hexToRgbaWithOpacity(customization.navbarBg, customization.navbarOpacity ?? 92)
          : hexToRgbaWithOpacity(customization.primaryColor || theme.primaryColor!, customization.navbarOpacity ?? 92);

        const tabBarBg = customization.tabBarBg
          ? hexToRgbaWithOpacity(customization.tabBarBg, customization.tabBarOpacity ?? 88)
          : navbarBg;

        return {
          ...theme,
          textColor: customization.textColor || theme.textColor,
          accentColor: customization.accentColor || customization.primaryColor || theme.accentColor,
          primaryColor: customization.primaryColor || theme.primaryColor,
          secondaryColor: customization.secondaryColor || customization.primaryColor || theme.secondaryColor,
          cardBg: customization.cardBg || navbarBg,
          cardGradientColor1: customization.cardGradientColor1 || theme.cardGradientColor1,
          cardGradientColor2: customization.cardGradientColor2 || theme.cardGradientColor2,
          cardGradientEnabled: customization.cardGradientEnabled ?? theme.cardGradientEnabled,
          navbarBg,
          tabBarBg,
        };
      }

      // Apply color transformations when colorSpace is enabled (TMI/HSV/RGB)

      const transformOptions: ColorTransformOptions = {
        colorSpace,
        temperatureLimit: customization.temperatureLimit ?? 50,
        magentaLimit: customization.magentaLimit ?? 50,
        intensityLimit: customization.intensityLimit ?? 50,
        saturationLimit: customization.backgroundSaturationLimit ?? 50,
        brightnessLimit: customization.backgroundBrightnessLimit ?? 50,
        redLimit: customization.redLimit ?? 50,
        greenLimit: customization.greenLimit ?? 50,
        blueLimit: customization.blueLimit ?? 50,
        contrast: customization.backgroundContrast ?? 50,
        hueGravity: customization.backgroundHueGravity ?? 0,
        graySaturation: customization.backgroundGraySaturation ?? 0,
        targetHue: customization.backgroundDefaultHue ?? 184,
      };

      // Transform ALL color properties
      const transformedTextColor = transformColor(customization.textColor || theme.textColor!, transformOptions);
      const transformedAccentColor = transformColor(customization.accentColor || customization.primaryColor || theme.accentColor!, transformOptions);
      const transformedPrimaryColor = transformColor(customization.primaryColor || theme.primaryColor!, transformOptions);
      const transformedSecondaryColor = transformColor(customization.secondaryColor || customization.primaryColor || theme.secondaryColor!, transformOptions);
      const transformedCardBg = customization.cardBg ? transformColor(customization.cardBg, transformOptions) : transformedPrimaryColor;
      const transformedCardGradientColor1 = customization.cardGradientColor1
        ? transformColor(customization.cardGradientColor1, transformOptions)
        : theme.cardGradientColor1;
      const transformedCardGradientColor2 = customization.cardGradientColor2
        ? transformColor(customization.cardGradientColor2, transformOptions)
        : theme.cardGradientColor2;

      // Transform navbar and tab bar colors, then apply opacity
      const transformedNavbarBgColor = customization.navbarBg
        ? transformColor(customization.navbarBg, transformOptions)
        : transformedPrimaryColor;
      const navbarBg = hexToRgbaWithOpacity(transformedNavbarBgColor, customization.navbarOpacity ?? 92);

      const transformedTabBarBgColor = customization.tabBarBg
        ? transformColor(customization.tabBarBg, transformOptions)
        : transformedPrimaryColor;
      const tabBarBg = customization.tabBarBg
        ? hexToRgbaWithOpacity(transformedTabBarBgColor, customization.tabBarOpacity ?? 88)
        : navbarBg;

      return {
        ...theme,
        textColor: transformedTextColor,
        accentColor: transformedAccentColor,
        primaryColor: transformedPrimaryColor,
        secondaryColor: transformedSecondaryColor,
        cardBg: transformedCardBg,
        cardGradientColor1: transformedCardGradientColor1,
        cardGradientColor2: transformedCardGradientColor2,
        cardGradientEnabled: customization.cardGradientEnabled ?? theme.cardGradientEnabled,
        navbarBg,
        tabBarBg,
      };
    }

    // Fallback: Use site theme from ThemeContext
    return theme;
  }, [theme, communityCustomization]);

  // STEP 1: Register theme from navigation state IMMEDIATELY (before post loads)
  useEffect(() => {
    const navState = location.state as any;
    if (navState?.themeContext?.colors) {
      setCurrentTheme({
        type: navState.themeContext.type,
        communityId: navState.themeContext.communityId,
        colors: navState.themeContext.colors
      });
    }
  }, [location.state, setCurrentTheme]);

  // STEP 2: Re-register when post loads with actual community data
  useEffect(() => {
    if (!post?.community?.id || !localTheme.primaryColor) return;

    setCurrentTheme({
      type: 'community',
      communityId: post.community.id,
      colors: {
        primaryColor: localTheme.primaryColor,
        secondaryColor: localTheme.secondaryColor || localTheme.primaryColor,
        textColor: localTheme.textColor || '#ffffff',
        accentColor: localTheme.accentColor || localTheme.primaryColor
      }
    });

      }, [post?.community?.id, localTheme.primaryColor, localTheme.secondaryColor, localTheme.textColor, localTheme.accentColor, setCurrentTheme]);

  // Cache the transformed community theme to sessionStorage for loading screen
  useEffect(() => {
    if (!post?.community?.id) return;

    // Cache the transformed theme for this community
    cacheTheme({
      textColor: localTheme.textColor!,
      accentColor: localTheme.accentColor!,
      cardBg: localTheme.cardBg,
      appBg: getBackground(localTheme as any, communityCustomization || {}, theme.appBg),
      navbarBg: localTheme.navbarBg || '',
      tabBarBg: localTheme.tabBarBg || '',
      primaryColor: localTheme.primaryColor!,
      secondaryColor: localTheme.secondaryColor!,
      colorSpace: 'Off'
    }, 'community', post.community.id);
  }, [localTheme, post?.community?.id, theme.appBg]);

  const themeColors = {
    navbarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
    accentColor: localTheme.accentColor!,
    textColor: localTheme.textColor!,
    tabBarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
    gradientFrom: localTheme.primaryColor!,
    gradientTo: localTheme.secondaryColor!
  };

  // Update animations when theme changes - always check cache first
  useEffect(() => {
    // Try to load from cache first (in case theme was updated while on another page)
    const cachedSiteTheme = getCachedTheme('site');
    if (cachedSiteTheme?.animations) {
      setThemeAnimations(cachedSiteTheme.animations);
    } else {
      // Fallback to preset animations
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [currentPreset]);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const COMMENTS_PER_PAGE = 25;

  // Listen for post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      if (post && post.id === data.postId) {
        setPost(prev => prev ? { ...prev, liked: data.liked, likes: data.likes } : null);
      }
    },
    onDislike: (data) => {
      if (post && post.id === data.postId) {
        setPost(prev => prev ? { ...prev, disliked: data.disliked, dislikes: data.dislikes } : null);
      }
    },
    postIds: post ? [post.id] : undefined
  });

  // Listen for comment updates from other pages/components
  useCommentSync({
    onLike: (data) => {
      setDisplayedComments(prev => prev.map(c =>
        c.id === data.commentId.toString()
          ? { ...c, liked: data.liked, likes: data.likes }
          : c
      ));
    },
    onDislike: (data) => {
      setDisplayedComments(prev => prev.map(c =>
        c.id === data.commentId.toString()
          ? { ...c, disliked: data.disliked, dislikes: data.dislikes }
          : c
      ));
    },
  });

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleLogout = async () => {
    logout();
  };

  useEffect(() => {
    async function loadData() {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      try {
        // Set auth state based on authUser
        if (authUser) {
          setIsAuthenticated(true);
          setShowLoginModal(false);
          // Site theme is already loaded by ThemeContext
        } else {
          // User is not authenticated, but we'll still load the post
          setIsAuthenticated(false);
          setShowLoginModal(true);
        }

        // Fetch community post data by communitySlug and postSlug (no auth required)
        const postResponse = await fetchWithAuth(`/api/community/slug/${communitySlug}/posts/${postSlug}`, {
          credentials: 'include'
        });

        if (postResponse.ok) {
          const postData = await postResponse.json();
          const post = postData.post;
          // Resolve author avatar
          if (post.author) {
            post.authorAvatar = resolveAvatar(post.author.profilePicture || post.author.image || post.authorAvatar);
          }
          setPost(normalizeLikeCounts(post));

          // Community theme is already loaded by ThemeContext via NavigationContext
          // No need to manually fetch and transform colors here

          // Fetch comments for this community post
          if (postData.post.community?.id && postData.post.id) {
            const commentsResponse = await fetchWithAuth(`/api/communities/${postData.post.community.id}/posts/${postData.post.id}/replies?limit=100`, {
              credentials: 'include'
            });

            if (commentsResponse.ok) {
              const commentsData = await commentsResponse.json();
              const replies = commentsData.replies || [];

              // Transform to modern pattern with numeric counts and boolean states
              const formattedComments = replies.map((reply: any) => ({
                id: reply.id,
                content: reply.content,
                author: reply.author?.profileName || reply.author?.name || reply.author?.username || 'Unknown',
                authorId: reply.author?.id || reply.authorId,
                authorUsername: reply.author?.username,
                authorAvatar: resolveAvatar(reply.author?.profilePicture || reply.author?.image),
                timestamp: reply.createdAt,
                // Backend already provides likes/dislikes as numbers and liked/disliked as booleans
                likes: typeof reply.likes === 'number' ? reply.likes : (reply._count?.likes || 0),
                dislikes: typeof reply.dislikes === 'number' ? reply.dislikes : (reply._count?.dislikes || 0),
                liked: typeof reply.liked === 'boolean' ? reply.liked : false,
                disliked: typeof reply.disliked === 'boolean' ? reply.disliked : false,
              }));

              setAllComments(formattedComments);
              setDisplayedComments(formattedComments.slice(0, COMMENTS_PER_PAGE));
            } else {
              console.error('Failed to fetch comments:', commentsResponse.status);
            }
          } else {
            console.error('Missing community ID or post ID for fetching comments:', {
              communityId: postData.post.community?.id,
              postId: postData.post.id
            });
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading post data:', error);
        setLoading(false);
      }
    }

    loadData();
  }, [communitySlug, postSlug, authUser, authLoading]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Theme updates are handled automatically by ThemeContext

  const loadMoreComments = () => {
    const nextPage = commentPage + 1;
    const startIndex = 0;
    const endIndex = nextPage * COMMENTS_PER_PAGE;
    setDisplayedComments(allComments.slice(startIndex, endIndex));
    setCommentPage(nextPage);
  };

  const handlePostComment = async () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    if (!newComment.trim() || !post) return;

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community?.id}/posts/${post.id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        const reply = data.reply; // Backend returns { reply }

        // Ensure reply has all required properties with defaults
        const formattedComment = {
          id: reply.id,
          content: reply.content || newComment.trim(),
          author: reply.author?.profileName || reply.author?.name || reply.author?.username || currentUser.displayName || currentUser.username,
          authorId: reply.author?.id || reply.authorId || currentUser.id,
          authorUsername: reply.author?.username || currentUser.username,
          authorAvatar: resolveAvatar(reply.author?.profilePicture || reply.author?.image || currentUser.avatar),
          timestamp: reply.createdAt || new Date().toISOString(),
          likes: 0,
          dislikes: 0,
          liked: false,
          disliked: false
        };
        setAllComments([formattedComment, ...allComments]);
        setDisplayedComments([formattedComment, ...displayedComments].slice(0, commentPage * COMMENTS_PER_PAGE));
        setNewComment('');
        if (post) {
          setPost({ ...post, comments: post.comments + 1 });
        }
      } else {
        const errorData = await response.json();
      }
    } catch (error) {
    }
  };

  const likePost = async () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    if (!post) return;

    // Capture initial state
    const wasLiked = post.liked;
    const initialLikes = post.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPost({ ...post, liked: newLiked, likes: newLikes });

    // Emit local event for same-page sync
    postEventBus.emitLike(post.id, newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community?.id}/posts/${post.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state
        setPost((prev) => prev ? { ...prev, liked: wasLiked, likes: initialLikes } : null);
        // Emit revert event
        postEventBus.emitLike(post.id, wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert to initial state
      setPost((prev) => prev ? { ...prev, liked: wasLiked, likes: initialLikes } : null);
      // Emit revert event
      postEventBus.emitLike(post.id, wasLiked, initialLikes);
    }
  };

  const dislikePost = async () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    if (!post) return;

    // Capture initial state
    const wasDisliked = post.disliked;
    const initialDislikes = post.dislikes || 0;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPost({
      ...post,
      disliked: newDisliked,
      dislikes: newDislikes
    });

    // Emit local event for same-page sync
    postEventBus.emitDislike(post.id, newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community?.id}/posts/${post.id}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert to initial state
        setPost((prev) => prev ? { ...prev, disliked: wasDisliked, dislikes: initialDislikes } : null);
        // Emit revert event
        postEventBus.emitDislike(post.id, wasDisliked || false, initialDislikes);
      }
    } catch (error) {
      // Revert to initial state
      setPost((prev) => prev ? { ...prev, disliked: wasDisliked, dislikes: initialDislikes } : null);
      // Emit revert event
      postEventBus.emitDislike(post.id, wasDisliked || false, initialDislikes);
    }
  };

  const handleShare = () => {
    if (!post) return;

    const url = post.slug && post.authorUsername
      ? `${window.location.origin}${getLegacyPostUrl(post.authorUsername, post.slug)}`
      : `${window.location.origin}/posts/${post.id}`;

    navigator.clipboard.writeText(url).then(() => {
      alert('Link copied to clipboard!');
      setShowShareModal(false);
    }).catch(err => {
      alert('Failed to copy link');
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed');
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  };

  const likeComment = useCallback(async (commentId: string) => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    if (!post?.community?.id) return;

    const comment = displayedComments.find(c => c.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialLiked = comment.liked || false;
    const initialLikes = comment.likes || 0;

    // Optimistic update
    const newLiked = !initialLiked;
    const newLikes = initialLiked ? initialLikes - 1 : initialLikes + 1;

    setDisplayedComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, liked: newLiked, likes: newLikes }
        : c
    ));

    // Emit event to sync across components
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/replys/${commentId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setDisplayedComments(prev => prev.map(c =>
          c.id === commentId
            ? { ...c, liked: initialLiked, likes: initialLikes }
            : c
        ));
        // Emit revert event
        commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setDisplayedComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, liked: initialLiked, likes: initialLikes }
          : c
      ));
      // Emit revert event
      commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
    }
  }, [currentUser, post, displayedComments]);

  const dislikeComment = useCallback(async (commentId: string) => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    if (!post?.community?.id) return;

    const comment = displayedComments.find(c => c.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialDisliked = comment.disliked || false;
    const initialDislikes = comment.dislikes || 0;

    // Optimistic update
    const newDisliked = !initialDisliked;
    const newDislikes = initialDisliked ? initialDislikes - 1 : initialDislikes + 1;

    setDisplayedComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, disliked: newDisliked, dislikes: newDislikes }
        : c
    ));

    // Emit event to sync across components
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/replys/${commentId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setDisplayedComments(prev => prev.map(c =>
          c.id === commentId
            ? { ...c, disliked: initialDisliked, dislikes: initialDislikes }
            : c
        ));
        // Emit revert event
        commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setDisplayedComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, disliked: initialDisliked, dislikes: initialDislikes }
          : c
      ));
      // Emit revert event
      commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
    }
  }, [currentUser, post, displayedComments]);

  const handleEditPost = useCallback((post: Post) => {
    setEditingPost(post);
    setShowModerateMenu(false);
  }, []);

  const handleSaveEdit = async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost || !currentUser) return;

    const permUser = {
      id: typeof currentUser.id === 'string' ? parseInt(currentUser.id) : currentUser.id,
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const editorType = getEditorType(editingPost as any, permUser);
    const endpoint = `/api/moderation/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        setPost(prev => prev ? {
          ...prev,
          ...updatedData
        } : prev);
        setEditingPost(null);
      }
    } catch (error) {
      throw error;
    }
  };

  const handlePinPost = useCallback(async (postToPin: Post) => {
    if (!currentUser) return;

    const permUser = {
      id: typeof currentUser.id === 'string' ? parseInt(currentUser.id) : currentUser.id,
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const endpoint = `/api/moderation/posts/${postToPin.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(prev => prev ? {
          ...prev,
          isPinned: data.pinned
        } as Post : prev);
        setShowModerateMenu(false);
      }
    } catch (error) {
    }
  }, [currentUser]);

  const handlePinToFeed = useCallback(async (postToPin: Post) => {
    if (!currentUser || !(currentUser as any).isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${postToPin.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(prev => prev ? {
          ...prev,
          isPinnedToFeed: data.pinnedToFeed
        } as Post : prev);
        setShowModerateMenu(false);
      }
    } catch (error) {
    }
  }, [currentUser]);

  const handleDeletePost = useCallback(async (postToDelete: Post) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = `/api/moderation/posts/${postToDelete.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setShowModerateMenu(false);
        navigate(-1);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete post');
      }
    } catch (error) {
      alert('Network error. Please try again.');
    }
  }, [navigate]);

  const handleToggleModerateMenu = useCallback(() => {
    setShowModerateMenu(prev => !prev);
  }, []);

  const handleCloseModerateMenu = useCallback(() => {
    setShowModerateMenu(false);
  }, []);

  const handleEditPostClick = useCallback(() => {
    if (post) handleEditPost(post);
  }, [post, handleEditPost]);

  const handlePinPostClick = useCallback(() => {
    if (post) handlePinPost(post);
  }, [post, handlePinPost]);

  const handlePinToFeedClick = useCallback(() => {
    if (post) handlePinToFeed(post);
  }, [post, handlePinToFeed]);

  const handleDeletePostClick = useCallback(() => {
    if (post) handleDeletePost(post);
  }, [post, handleDeletePost]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="relative" style={{ background: getBackground(localTheme as any, communityCustomization || {}, theme.appBg), minHeight: backgroundHeight }}>
      <AnimationLayer animations={themeAnimations} isMobile={false} bubbleColor={localTheme.accentColor} />

      <PostDetailNavbar
        onBack={() => navigate(-1)}
        onLogout={handleLogout}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        theme={localTheme}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
      />

      {/* Wavy Line Separator */}
      <div className="w-full flex justify-center py-4">
        <svg width="80%" height="20" viewBox="0 0 800 20" preserveAspectRatio="none">
          <path
            d="M0,10 Q200,0 400,10 T800,10"
            stroke={localTheme.accentColor}
            strokeWidth="3"
            fill="none"
          />
        </svg>
      </div>

      {/* Main Content */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: `${scaleH(80, scale.h)}px`
      }}>
        {!post ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2" style={{ color: localTheme.textColor }}>Post not found</h2>
              <p style={{ color: localTheme.textColor + '99' }}>This post may have been deleted or doesn't exist.</p>
            </div>
          </div>
        ) : (
          <div style={{
            width: '100%',
            maxWidth: `${scaleW(1200, scale.w)}px`,
            padding: `0 ${scaleW(64, scale.w)}px`
          }}>
            {/* Post Card */}
            <div className="rounded-xl p-6 mb-6 backdrop-blur-sm border relative" style={{
              background: getCardBackground(localTheme as any, communityCustomization || {}, theme.cardBg),
              borderColor: localTheme.accentColor + '40'
            }}>
              {/* Post Badges */}
              {post && <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />}

              {/* Moderate Dropdown */}
              {currentUser && post && canModeratePost(post as any, {
                id: typeof currentUser.id === 'string' ? parseInt(currentUser.id) : currentUser.id,
                username: currentUser.username,
                isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
                isModerator: (currentUser as any).isSiteModerator || false
              }) && (
                <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                  <button
                    onClick={handleToggleModerateMenu}
                    className="p-1.5 rounded-lg transition-all hover:opacity-80"
                    style={{
                      backgroundColor: localTheme.accentColor + '20',
                      color: localTheme.textColor
                    }}
                  >
                    <MoreVertical size={16} />
                  </button>

                  {showModerateMenu && (
                    <>
                      <div
                        className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                        style={{
                          backgroundColor: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                          borderColor: localTheme.accentColor + '60',
                          border: '1px solid',
                          zIndex: 1001
                        }}
                      >
                        <button
                          onClick={handleEditPostClick}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Edit3 size={16} />
                          Edit
                        </button>
                        <button
                          onClick={handlePinPostClick}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: localTheme.textColor,
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Pin size={16} />
                          {(post as any).isPinned ? 'Unpin' : 'Pin'}
                        </button>
                        {(currentUser as any).isSuperAdmin && (
                          <button
                            onClick={handlePinToFeedClick}
                            className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                            style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Pin size={16} />
                            {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                          </button>
                        )}
                        <button
                          onClick={handleDeletePostClick}
                          className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                          style={{
                            color: '#ef4444',
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef444420';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      </div>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 90 }}
                        onClick={handleCloseModerateMenu}
                      />
                    </>
                  )}
                </div>
              )}

              {/* Post Header */}
              <div className="flex items-center gap-3 mb-4">
                <Link to={post.authorUsername ? `/user/${post.authorUsername}` : '#'} className="w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80" style={{
                  background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                }}>
                  <span style={{ color: localTheme.textColor }}>
                    {typeof post.author === 'string' ? post.author[0] : ((post.author as any)?.username?.[0] || (post.author as any)?.name?.[0] || 'U')}
                  </span>
                </Link>
            <div className="flex-1">
              <p className="font-semibold" style={{ color: localTheme.textColor }}>
                {typeof post.author === 'string' ? post.author : ((post.author as any)?.profileName || (post.author as any)?.name || (post.author as any)?.username || 'Unknown')}
              </p>
              {post.authorUsername && (
                <p className="text-sm" style={{ color: localTheme.textColor + 'aa' }}>@{post.authorUsername}</p>
              )}
            </div>
            <span className="text-sm" style={{ color: localTheme.textColor + '99', position: 'relative', left: '-2.5rem' }}>{formatTimestamp(post.createdAt)}</span>
          </div>

          {/* Post Title */}
          {post.title && (
            <h2 className="text-2xl font-bold mb-3" style={{ color: localTheme.textColor }}>{post.title}</h2>
          )}

          {/* Post Media Above Content */}
          {(post.mediaUrl || post.imageUrl) && post.aboveContent && (
            <img src={post.mediaUrl || post.imageUrl} alt="Post media" className="w-full rounded-lg mb-4" />
          )}

          {/* Post Content */}
          <p className="mb-4 text-lg leading-relaxed" style={{ color: localTheme.textColor + 'ee' }}>{post.content}</p>

          {/* Post Media Below Content */}
          {(post.mediaUrl || post.imageUrl) && !post.aboveContent && (
            <img src={post.mediaUrl || post.imageUrl} alt="Post media" className="w-full rounded-lg mb-4" />
          )}

          {/* Post Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: localTheme.accentColor + '30',
                    color: localTheme.textColor
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Post Actions */}
          <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
            <div className="flex items-center gap-6">
              <button
                onClick={likePost}
                className="flex items-center gap-2 transition-colors"
                style={{ color: post.liked ? '#f87171' : localTheme.textColor + '99' }}
              >
                <Heart size={20} className={post.liked ? "fill-current" : ""} />
                <span>{post.likes}</span>
              </button>
              <button
                onClick={dislikePost}
                className="flex items-center gap-2 transition-colors"
                style={{ color: post.disliked ? '#f97316' : localTheme.textColor + '99' }}
              >
                <ThumbsDown size={20} className={post.disliked ? "fill-current" : ""} />
                <span>{post.dislikes || 0}</span>
              </button>
              <div className="flex items-center gap-2" style={{ color: localTheme.textColor + '99' }}>
                <span>{post.comments} comments</span>
              </div>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 transition-colors hover:text-blue-400"
              style={{ color: localTheme.textColor + '99' }}
            >
              <Share2 size={20} />
            </button>
          </div>
        </div>

        {/* Comment Input */}
        <div className="rounded-xl p-4 mb-6 backdrop-blur-sm border" style={{
          background: getCardBackground(localTheme as any, communityCustomization || {}),
          borderColor: localTheme.accentColor + '40'
        }}>
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
              background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
            }}>
              <span style={{ color: localTheme.textColor }}>{currentUser?.displayName?.[0] || currentUser?.username[0] || 'U'}</span>
            </div>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePostComment()}
                placeholder="Write a comment..."
                className="flex-1 px-4 py-2 rounded-lg border outline-none transition-colors"
                style={{
                  backgroundColor: localTheme.accentColor + '10',
                  borderColor: localTheme.accentColor + '40',
                  color: localTheme.textColor
                }}
              />
              <button
                onClick={handlePostComment}
                className="px-4 py-2 rounded-lg transition-all hover:opacity-80"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold mb-4" style={{ color: localTheme.textColor }}>
            Comments ({allComments.length})
          </h3>

          {displayedComments.map((comment) => (
            <div
              key={comment.id}
              className="rounded-xl p-4 backdrop-blur-sm border"
              style={{
                background: getCardBackground(localTheme as any, communityCustomization || {}, theme.cardBg),
                borderColor: localTheme.accentColor + '20'
              }}
            >
              <div className="flex gap-3">
                <Link to={comment.authorUsername ? `/user/${comment.authorUsername}` : '#'} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80" style={{
                  background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                }}>
                  {comment.authorAvatar ? (
                    <img src={comment.authorAvatar} alt={comment.author} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span style={{ color: localTheme.textColor }}>{comment.author?.[0] || 'U'}</span>
                  )}
                </Link>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p key="author" className="font-semibold" style={{ color: localTheme.textColor }}>{comment.author || 'Unknown'}</p>
                    {comment.authorUsername && (
                      <p key="username" className="text-sm" style={{ color: localTheme.textColor + 'aa' }}>@{comment.authorUsername}</p>
                    )}
                    <span key="timestamp" className="text-xs" style={{ color: localTheme.textColor + '66' }}>{formatTimestamp(comment.timestamp)}</span>
                  </div>
                  <p className="mb-2" style={{ color: localTheme.textColor + 'dd' }}>{comment.content}</p>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => likeComment(comment.id)}
                      className="flex items-center gap-1 transition-colors"
                      style={{ color: comment.liked ? '#ef4444' : localTheme.textColor + '66' }}
                    >
                      <Heart size={14} className={comment.liked ? "fill-current" : ""} />
                      <span className="text-xs">{comment.likes}</span>
                    </button>
                    <button
                      onClick={() => dislikeComment(comment.id)}
                      className="flex items-center gap-1 transition-colors"
                      style={{ color: comment.disliked ? '#f97316' : localTheme.textColor + '66' }}
                    >
                      <ThumbsDown size={14} className={comment.disliked ? "fill-current" : ""} />
                      <span className="text-xs">{comment.dislikes}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {displayedComments.length < allComments.length && (
            <button
              onClick={loadMoreComments}
              className="w-full py-3 rounded-lg transition-all hover:opacity-80 flex items-center justify-center gap-2"
              style={{
                backgroundColor: localTheme.accentColor + '20',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
            >
              Load More Comments
              <ChevronDown size={18} />
            </button>
          )}
        </div>
          </div>
        )}
      </div>


      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost as any}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

    </div>
  );
}
